import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import chillwerkLogo from "@/assets/chillwerk-logo.png";

export const UGCVideoSection = () => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Auto-play when in view, pause when out — with mobile-safe handling
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Ensure muted before play attempt (required for autoplay on iOS/Safari)
          video.muted = true;
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => setIsPlaying(true))
              .catch(() => {
                // Autoplay blocked — show play button so user can tap
                setIsPlaying(false);
                setHasInteracted(true);
              });
          }
        } else {
          video.pause();
          setIsPlaying(false);
        }
      },
      { threshold: 0.25 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    setHasInteracted(true);
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    setHasInteracted(true);
    const newMuted = !video.muted;
    // Remove the muted attribute entirely (some browsers ignore the property change otherwise)
    if (newMuted) {
      video.setAttribute('muted', '');
      video.muted = true;
    } else {
      video.removeAttribute('muted');
      video.muted = false;
      video.volume = 1;
    }
    setIsMuted(newMuted);
    // Resume playback in user-gesture context
    const p = video.play();
    if (p !== undefined) {
      p.then(() => setIsPlaying(true)).catch((err) => {
        console.warn('Video play after unmute failed:', err);
      });
    }
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-16 md:py-24 overflow-hidden bg-[hsl(35_30%_97%)]"
    >
      {/* Soft ambient tints */}
      <div className="absolute top-1/4 left-[5%] w-[28rem] h-[28rem] bg-cyan/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-[5%] w-[24rem] h-[24rem] bg-yellow/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* LEFT — Copy in frosted glass card */}
          <div className="order-2 lg:order-1 text-center lg:text-left space-y-6 relative overflow-hidden rounded-[28px] bg-gradient-to-br from-cyan-light/60 via-white to-yellow/10 border border-cyan/25 shadow-[0_20px_60px_-25px_hsl(var(--primary)/0.45)] px-5 py-8 sm:px-6 sm:py-10 md:px-10 md:py-12 min-w-0">


            <h2 className="text-[clamp(1.75rem,7vw,2.25rem)] md:text-5xl lg:text-6xl font-heading font-black leading-[1.08] text-foreground break-words">
              Zo werkt{" "}
              <span className="relative inline-block max-w-full">
                <span className="relative z-10">ChillWerk.nl</span>
                <span className="absolute left-0 -bottom-1 w-full h-3 md:h-4 bg-yellow -z-0 rounded-sm" />
              </span>
            </h2>


            <p className="text-lg md:text-xl text-foreground/70 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              Geen sollicitatiebrieven. Gewoon{" "}
              <span className="font-bold text-foreground">jezelf laten zien</span> — en werkgevers komen ook naar jou.
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center lg:justify-start pt-4 w-full">
              <Button
                variant="cta"
                size="lg"
                className="shadow-xl hover:shadow-2xl w-full sm:w-auto whitespace-normal text-center"
                onClick={() => navigate('/signup/kandidaat?src=video')}
              >
                Zet jezelf op de radar
              </Button>
            </div>



          </div>

          {/* RIGHT — Video */}
          <div className="order-1 lg:order-2 relative mx-auto w-full max-w-[340px] sm:max-w-[380px] lg:rotate-1">

            {/* Floating decoration */}
            <div className="absolute -top-6 -left-6 text-5xl animate-bounce z-20 pointer-events-none" style={{ animationDuration: '3s' }}>
              ✨
            </div>
            <div className="absolute -bottom-4 -right-4 text-4xl animate-bounce z-20 pointer-events-none" style={{ animationDuration: '3.5s', animationDelay: '0.5s' }}>
              🎯
            </div>

            {/* Phone-like frame */}
            <div className="relative group">
              {/* Outer glow */}
              <div className="absolute -inset-3 bg-gradient-to-br from-primary/40 via-cyan-light/30 to-yellow/30 rounded-[2.5rem] blur-2xl opacity-70 group-hover:opacity-100 transition-opacity duration-700" />

              {/* Frame */}
              <div className="relative rounded-[2rem] overflow-hidden bg-foreground p-2 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.45)] ring-1 ring-foreground/10">
                <div className="relative rounded-[1.5rem] overflow-hidden bg-black aspect-[9/16]">
                  {/* Poster fallback always visible until video loads */}
                  {!isLoaded && !hasError && (
                    <img
                      src="/videos/ugc-0423-poster.jpg"
                      alt="ChillWerk.nl video preview"
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                  )}

                  {/* Error fallback — show poster only (behind controls) */}
                  {hasError && (
                    <img
                      src="/videos/ugc-0423-poster.jpg"
                      alt="ChillWerk.nl video preview"
                      className="absolute inset-0 w-full h-full object-cover z-0"
                    />
                  )}

                  <video
                    ref={videoRef}
                    poster="/videos/ugc-0423-poster.jpg"
                    className="w-full h-full object-cover relative z-[1]"
                    playsInline
                    loop
                    muted
                    preload="metadata"
                    onClick={togglePlay}
                    onLoadedData={() => setIsLoaded(true)}
                    onCanPlay={() => setIsLoaded(true)}
                    onError={() => setHasError(true)}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  >
                    {/* Mobile-optimized version first (2.5MB), desktop fallback (53MB) */}
                    <source src="/videos/ugc-0423-mobile.mp4" type="video/mp4" media="(max-width: 1024px)" />
                    <source src="/videos/ugc-0423-mobile.mp4" type="video/mp4" />
                  </video>

                  {/* Play overlay (only show when paused after interaction) */}
                  {!isPlaying && hasInteracted && !hasError && (
                    <button
                      onClick={togglePlay}
                      className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm transition-opacity z-10"
                      aria-label="Play video"
                    >
                      <div className="w-20 h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl hover:scale-110 transition-transform">
                        <Play className="w-9 h-9 text-foreground ml-1" fill="currentColor" />
                      </div>
                    </button>
                  )}

                  {/* Top gradient */}
                  <div className="absolute top-0 inset-x-0 h-20 bg-gradient-to-b from-black/50 to-transparent pointer-events-none" />

                  {/* Bottom controls */}
                  <div className="absolute bottom-3 right-3 flex gap-2 z-20">
                    <button
                      onClick={toggleMute}
                      className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg hover:bg-white hover:scale-110 transition-all"
                      aria-label={isMuted ? "Unmute" : "Mute"}
                    >
                      {isMuted ? (
                        <VolumeX className="w-4 h-4 text-foreground" />
                      ) : (
                        <Volume2 className="w-4 h-4 text-foreground" />
                      )}
                    </button>
                    <button
                      onClick={togglePlay}
                      className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg hover:bg-white hover:scale-110 transition-all"
                      aria-label={isPlaying ? "Pause" : "Play"}
                    >
                      {isPlaying ? (
                        <Pause className="w-4 h-4 text-foreground" fill="currentColor" />
                      ) : (
                        <Play className="w-4 h-4 text-foreground ml-0.5" fill="currentColor" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Floating chip — bottom left */}
              <div className="hidden md:flex absolute -bottom-5 -left-8 items-center gap-2 px-4 py-2.5 rounded-2xl bg-white shadow-xl border border-border/50 z-20">
                <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center overflow-hidden ring-2 ring-white">
                  <img src={chillwerkLogo} alt="Chillwerk.nl logo" className="w-full h-full object-cover" />
                </div>
                <div className="text-left">
                  <div className="text-[11px] text-muted-foreground leading-tight">Chillwerk</div>
                  <div className="text-xs font-bold leading-tight">@chillwerk.nl</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </section>
  );
};
